<template>
	<page-container :page="page">
		<main-content
			:title="page.title || page.header"
			:subtitle="page.subtitle"
			:content="page.content"
			:introduction-content="page.introductionContent"
		/>

		<section class="main-content news-section background-light">
			<div class="row flex-row">
				<div class="flex-row column6">
					<div v-for="item in news.slice(0, 1)" :key="item.ID" class="block-item white">
						<nuxt-link :to="localePath(`/${item.filename}`)">
							<div class="block-item-content">
								<span v-if="item.date" class="event-date">
									{{ $d(new Date(item.date.date.split(' ')[0]), 'short') }}
								</span>
								<h3>{{ item.title || item.header }}</h3>
								<div v-parse-links v-html="item.overviewContent" />
								<span class="button-arrow">
									{{ $t('readMore') }}
									<font-awesome-icon icon="fa-light fa-arrow-right-long" size="1x" />
								</span>
							</div>
							<picture>
								<source
									v-if="item.overviewImageWebp"
									:srcset="item.overviewImageWebp"
									type="image/webp"
								/>
								<source :srcset="item.overviewImage" />
								<img loading="lazy" :src="item.overviewImage" :alt="item.overviewImageAlt" />
							</picture>
						</nuxt-link>
					</div>
				</div>
				<div class="flex-row column6">
					<div v-for="item in news.slice(1, 5)" :key="item.ID" class="block-item white">
						<nuxt-link :to="localePath(`/${item.filename}`)">
							<div class="block-item-content">
								<span v-if="item.date" class="event-date">
									{{ $d(new Date(item.date.date.split(' ')[0]), 'short') }}
								</span>
								<h3>{{ item.title || item.header }}</h3>
								<div v-parse-links v-html="item.overviewContent" />
								<span class="button-arrow">
									{{ $t('readMore') }}
									<font-awesome-icon icon="fa-light fa-arrow-right-long" size="1x" />
								</span>
							</div>
							<picture>
								<source
									v-if="item.overviewImageWebp"
									:srcset="item.overviewImageWebp"
									type="image/webp"
								/>
								<source :srcset="item.overviewImage" />
								<img loading="lazy" :src="item.overviewImage" :alt="item.overviewImageAlt" />
							</picture>
						</nuxt-link>
					</div>
				</div>
				<div class="flex-row column12">
					<nuxt-link
						v-for="item in news.slice(5, 100)"
						:key="item.ID"
						:to="localePath(`/${item.filename}`)"
						class="block-item white"
					>
						<div class="block-item-content">
							<span v-if="item.date" class="event-date">
								{{ $d(new Date(item.date.date.split(' ')[0]), 'short') }}
							</span>
							<h3>{{ item.title || item.header }}</h3>
							<div v-parse-links v-html="item.overviewContent" />
							<span class="button-arrow">
								{{ $t('readMore') }}
								<font-awesome-icon icon="fa-light fa-arrow-right-long" size="1x" />
							</span>
						</div>
						<picture>
							<source v-if="item.overviewImageWebp" :srcset="item.overviewImageWebp" type="image/webp" />
							<source :srcset="item.overviewImage" />
							<img loading="lazy" :src="item.overviewImage" :alt="item.overviewImageAlt" />
						</picture>
					</nuxt-link>
				</div>
			</div>
		</section>

		<contentblocks
			v-if="page && page.contentblocks && page.contentblocks.length"
			:items="page.contentblocks"
			:content-blocks-content="page.sectionContentblocksContent"
			:content-blocks-subtitle="page.sectionContentblocksTitle"
		/>
		<frequently-asked-questions
			v-if="page && page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
		<pre-footer
			v-if="page && page.footerSection && page.footerSection.length"
			:footer-section="page.footerSection[0]"
		/>
		<newsletter
			v-if="page && page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const { data: news } = await useWebsiteFetch('news', {
	query: { language: locale.value },
	key: `${locale.value}/news`,
});
</script>

<style lang="scss">
.news {
	.column12 {
		padding: 40px;
		background: #fff;
		margin: 0 auto 40px;
		box-shadow: 0 0 20px rgb(0 0 0 / 5%);
	}

	.news-block,
	.news-block:hover {
		color: #000;
	}

	.news-block {
		span,
		h3 {
			color: $cta-color;
		}

		p,
		h3 {
			margin: 0 0 10px;
		}

		.news-image {
			padding: 0 20px 0 0;
			max-width: 30%;
		}

		.news-content {
			padding: 0 20px;
			max-width: 70%;
		}
	}
}

.block-item a,
a.block-item {
	color: #fff;
}

.event-date {
	color: #fff;
	font-size: 14px;
	margin: 0 0 10px;
	display: inline-block;
	width: 100%;
	letter-spacing: 2px;
}

.column12 {
	&.flex-row {
		justify-content: flex-start;
		gap: 25px;
	}

	.block-item {
		width: 23.5%;
	}
}

.news-section {
	.flex-row {
		overflow: hidden;
	}
}

@media (max-width: 1380px) {
	.block-item-content {
		padding: 25px;
		font-size: 14px;
	}
}

@media (max-width: 920px) {
	.column12 {
		.block-item {
			width: 48%;
		}
	}
}

@media (max-width: 680px) {
	.column12 {
		justify-content: space-between;

		.block-item {
			width: 100%;
		}
	}
}

@media (max-width: 768px) {
	.news {
		.column12 {
			padding: 20px;
		}

		.news-block {
			.news-content {
				max-width: 100%;
			}

			.news-image {
				max-width: 100%;
				padding: 20px;
			}
		}
	}
}
</style>
